/**
 * Bottomsheet Component
 * A lightweight, reusable bottom sheet overlay component
 *
 * Expects HTML structure from bottomsheet.liquid snippet
 *
 * Usage:
 *   const sheet = new Bottomsheet({
 *     onOpen: (content) => {},
 *     onClose: () => {}
 *   });
 *
 *   sheet.show(htmlContent, { title: 'My Title' });
 *   sheet.hide();
 */

class Bottomsheet {
    constructor(options = {}) {
        this.options = {
            onOpen: options.onOpen || null,
            onClose: options.onClose || null,
            animationDuration: options.animationDuration || 300,
            ...options,
        };

        this.isOpen = false;
        this.isAnimating = false;
        this.currentContent = null;

        this.init();
    }

    init() {
        this.cacheElements();
        this.attachEventListeners();
    }

    cacheElements() {
        // Get elements from existing Liquid template
        this.overlay = document.querySelector("[data-bs-overlay]");
        this.container = document.querySelector("[data-bs-container]");
        this.closeBtn = document.querySelector("[data-bs-close]");
        this.contentArea = document.querySelector("[data-bs-content]");

        if (!this.overlay || !this.container) {
            console.error(
                "Bottomsheet HTML structure not found. Make sure bottomsheet.liquid snippet is included in layout."
            );
        }
    }

    attachEventListeners() {
        // Close button
        this.closeBtn.addEventListener("click", () => this.hide());

        // Overlay click (outside)
        this.overlay.addEventListener("click", (e) => {
            if (e.target === this.overlay) {
                this.hide();
            }
        });

        // Keyboard escape
        document.addEventListener("keydown", (e) => {
            if (e.key === "Escape" && this.isOpen) {
                this.hide();
            }
        });

        // Prevent scroll when open
        this.overlay.addEventListener(
            "touchmove",
            (e) => {
                if (e.target === this.overlay) {
                    e.preventDefault();
                }
            },
            { passive: false }
        );
    }

    /**
     * Show the bottomsheet with content
     * @param {HTMLElement|string} content - Content to display
     * @param {Object} options - Display options
     * @param {string} options.theme - CSS theme class name
     * @param {string} options.primaryColor - Primary color (hex or rgb)
     * @param {string} options.secondaryColor - Secondary color (hex or rgb)
     */
    show(content, options = {}) {
        if (this.isOpen || this.isAnimating) return;

        this.isAnimating = true;

        // Apply colors
        if (options.primaryColor) {
            this.container.style.setProperty('--bs-primary-color', options.primaryColor);
            this.container.style.setProperty('--bs-close-bg-color', options.primaryColor);
            this.container.style.setProperty('--bs-close-icon-color', options.primaryColor);
        }
        if (options.secondaryColor) {
            this.container.style.setProperty('--bs-secondary-color', options.secondaryColor);
        }

        // Apply theme
        if (options.theme) {
            this.container.classList.add(`bs-theme-${options.theme}`);
        }

        // Set content
        if (typeof content === "string") {
            this.contentArea.innerHTML = content;
        } else if (content instanceof HTMLElement) {
            this.contentArea.innerHTML = "";
            this.contentArea.appendChild(content);
        }

        this.currentContent = content;

        // Show sheet with reflow to trigger animation
        this.overlay.classList.add("active");
        document.body.style.overflow = "hidden";

        // Force reflow and trigger animation
        void this.container.offsetHeight;
        
        this.isOpen = true;
        this.isAnimating = false;

        // Focus first focusable element
        setTimeout(() => {
            const focusable = this.contentArea.querySelector(
                'input, button, textarea, select, [tabindex]:not([tabindex="-1"])'
            );
            if (focusable) focusable.focus();
        }, 100);

        if (this.options.onOpen) {
            this.options.onOpen(this.currentContent);
        }
    }

    /**
     * Hide the bottomsheet
     */
    hide() {
        if (!this.isOpen || this.isAnimating) return;

        this.isAnimating = true;
        this.overlay.classList.add("closing");

        setTimeout(() => {
            this.overlay.classList.remove("active", "closing");
            document.body.style.overflow = "";
            this.isOpen = false;
            this.isAnimating = false;

            // Clear theme class
            this.container.className = "bs-container";

            // Clear colors
            this.container.style.removeProperty('--bs-primary-color');
            this.container.style.removeProperty('--bs-secondary-color');

            // Clear content
            this.contentArea.innerHTML = "";

            if (this.options.onClose) {
                this.options.onClose();
            }
        }, this.options.animationDuration);
    }

    /**
     * Update the content while open
     * @param {HTMLElement|string} content - New content to display
     */
    updateContent(content) {
        if (typeof content === "string") {
            this.contentArea.innerHTML = content;
        } else if (content instanceof HTMLElement) {
            this.contentArea.innerHTML = "";
            this.contentArea.appendChild(content);
        }
        this.currentContent = content;
    }

    /**
     * Destroy the component and cleanup
     */
    destroy() {
        document.removeEventListener("keydown", this.handleEscape);
        this.overlay.remove();
        this.isOpen = false;
    }
}

// Initialize global instance when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
    if (!window.bottomsheet) {
        window.bottomsheet = new Bottomsheet();
    }
});

// Export for module usage
if (typeof module !== "undefined" && module.exports) {
    module.exports = Bottomsheet;
}
